<template>
  <div
    v-if="rowsList.length > 0"
    :data-parent-path="dataParentPath"
    :data-cell="dataCell || undefined"
    :data-row="dataCell ? rowIndex : undefined"
    :data-col="dataCell ? 0 : undefined"
    :class="cellClass"
    :style="isHidden ? { display: 'none' } : undefined"
  >
    <PivotRevealer
      v-if="isRevealed"
      :level
      :opened="isOpened"
      :path="dataPath"
      @click="handleReveal"
    />
    <PivotAssetLink
      v-if="isLinkableNode"
      :name="captionText"
      :style="labelStyle"
      @click="$emit('click:text', caption)"
    />
    <div
      v-else
      :class="isClickable && CLICKABLE_TEXT_CLASS"
      :style="labelStyle"
      @click="isClickable && $emit('click:text', caption)"
    >
      {{ captionText }}
    </div>
  </div>
  <PivotValueCell
    v-for="cell in valueCells"
    :key="cell.key"
    :text="cell.text"
    :raw-value="cell.rawValue"
    :field="cell.field"
    :parent-path="dataParentPath"
    :data-cell
    :hidden="isHidden"
    :row="rowIndex"
    :col="cell.col"
    :total="isRevealed && isOpened"
    :sign-colored="cell.signColored"
    :is-clickable-value="isClickableValue"
    @click="amount => $emit('click:value', cell.dimensions, amount)"
  />
</template>

<script setup lang="ts">
import { computed, inject, nextTick } from 'vue'

import { PivotTableClickValueData, PivotTableData, PivotTableValueRenderers } from '@types'

import {
  CLICKABLE_NODES_PROVIDE_KEY,
  CLICKABLE_TEXT_CLASS,
  CLICKABLE_VALUES_PROVIDE_KEY,
  DEEP_MARGIN,
  LINKABLE_NODES_PROVIDE_KEY,
  PATH_DELIMITER,
  RENDERERS_PROVIDE_KEY,
  SIGN_COLORED_FIELDS_PROVIDE_KEY,
} from '@/components/DS/patterns/Pivot/utils/const'
import { PivotedDataParentPathEntry, addOpenedRow, getIsOpened, getValuesByColumns, removeOpenedRow, renderValue } from '@/components/DS/patterns/Pivot/utils/query'
import { cn } from '@/helpers/cn'

import PivotAssetLink from './primitives/PivotAssetLink.vue'
import PivotRevealer from './primitives/PivotRevealer.vue'
import PivotValueCell from './primitives/PivotValueCell.vue'

type LoadChildren = (parentPath: PivotedDataParentPathEntry[], pathKey: string) => Promise<PivotTableData[]>

type Props = {
  item: PivotTableData
  columns: PivotTableData[]
  colsList?: string[]
  rowsList?: string[]
  valuesList?: string[]
  gridRef?: HTMLDivElement | null
  /** This row's `data-row` — the `v-for` index over `PivotContent`'s `data`. */
  rowIndex: number
}

type Emits = {
  'click:text': [data: string]
  'click:value': [data: PivotTableClickValueData, amount: number]
}

const { item, columns, colsList = [], rowsList = [], valuesList = [], gridRef } = defineProps<Props>()
defineEmits<Emits>()

const openedRows = inject<{ value: string[] }>('openedRows')
const loadChildren = inject<LoadChildren | undefined>('loadChildren', undefined)
const childrenCache = inject<Map<string, PivotTableData[]> | undefined>('childrenCache', undefined)
const loadingPaths = inject<Set<string> | undefined>('loadingPaths', undefined)
const linkableNodes = inject(LINKABLE_NODES_PROVIDE_KEY, [])
const clickableNodes = inject(CLICKABLE_NODES_PROVIDE_KEY, [])
const renderers = inject(RENDERERS_PROVIDE_KEY, computed<PivotTableValueRenderers>(() => ({})))
const signColoredFields = inject(SIGN_COLORED_FIELDS_PROVIDE_KEY, [])
const isClickableValue = inject(CLICKABLE_VALUES_PROVIDE_KEY, false)

const path = computed(() => rowsList.map(row => item[row]).filter(Boolean))
const level = computed(() => path.value.length - 1)
const caption = computed(() => path.value.at(-1))

const dataPath = computed(() => level.value >= 0 ? path.value.join(PATH_DELIMITER) : undefined)
const dataParentPath = computed(() => level.value > 0 ? path.value.slice(0, level.value).join(PATH_DELIMITER) : undefined)

const isOpened = computed(() => getIsOpened(dataPath.value, openedRows))
const isParentOpened = computed(() => getIsOpened(dataParentPath.value, openedRows))

const isHidden = computed(() => level.value > 0 && !isParentOpened.value)
const dataCell = computed(() => level.value === 0 || isParentOpened.value)

// content-visibility/contain-intrinsic-height: see PivotValueCell's comment — keeps every row
// mounted, letting the browser skip layout/paint for off-screen rows.
const cellClass = computed(() => cn(
  'py-3.5 px-4 relative flex items-center justify-end font-ds text-foreground text-sm sm:text-xs text-right overflow-hidden font-semibold cursor-cell',
  'outline outline-border/30',
  'sticky left-0 justify-start text-left z-10',
  isRevealed.value && isOpened.value ? 'bg-[#fafaff] dark:bg-[#151520]' : 'bg-card data-row-hovered:bg-[#fafaff] data-row-hovered:dark:bg-[#151520]',
  '[content-visibility:auto] [contain-intrinsic-height:auto_32px]',
))

const isLeaf = computed(() => level.value >= rowsList.length - 1)
const isRevealed = computed(() => level.value >= 0 && rowsList.length > 1 && !isLeaf.value)
const fieldKey = computed(() => rowsList[level.value])
const isClickable = computed(() => !!fieldKey.value && clickableNodes.includes(fieldKey.value))
const isLinkableNode = computed(() => !!fieldKey.value && linkableNodes.includes(fieldKey.value))

// A leaf row has no revealer, so its label carries the same indent instead, to keep edges aligned.
const labelStyle = computed(() => ({
  marginLeft: isLeaf.value ? `${level.value * DEEP_MARGIN}px` : undefined,
}))

const captionText = computed(() => fieldKey.value ? renderValue(caption.value, fieldKey.value, renderers.value, 'header-row', isRevealed.value) : caption.value)

type ValueCellSpec = { key: string, text: string, rawValue: unknown, field: string, dimensions: PivotTableClickValueData, signColored: boolean, col: number }

// Modern nests every row level into one label column, so `col` always starts right after it.
const ROW_LABEL_COL_COUNT = 1

// This row's dimensions up to its depth — a cell click adds its own column dimensions on top.
const rowDimensions = computed<PivotTableClickValueData>(() => Object.fromEntries(
  rowsList.filter(row => item[row] !== undefined).map(row => [row, item[row]]),
))

const valueCells = computed<ValueCellSpec[]>(() => {
  const cells: ValueCellSpec[] = []
  const valuesByColumns = getValuesByColumns(item, columns, colsList, valuesList)
  let col = ROW_LABEL_COL_COUNT
  for (const { value, valueField, path } of valuesByColumns) {
    const colDimensions = Object.fromEntries(colsList.map((colField, index) => [colField, path[index]]))
    cells.push({
      key: `col:${path.join(PATH_DELIMITER)}`,
      text: renderValue(value, valueField, renderers.value),
      rawValue: value,
      field: valueField,
      dimensions: { ...rowDimensions.value, ...colDimensions },
      signColored: signColoredFields.includes(valueField),
      col: col++,
    })
  }
  for (const field of valuesList) {
    cells.push({
      key: `total:${field}`,
      text: renderValue(item[field], field, renderers.value),
      rawValue: item[field],
      field,
      dimensions: rowDimensions.value,
      signColored: signColoredFields.includes(field),
      col: col++,
    })
  }
  return cells
})

const handleReveal = async (event: MouseEvent) => {
  event.stopPropagation()
  event.preventDefault()
  const pathKey = dataPath.value
  if (pathKey && loadingPaths?.has(pathKey)) return
  const revealerEl = (event.target as HTMLElement)?.closest('[data-revealer]')

  if (pathKey && getIsOpened(pathKey, openedRows)) {
    removeOpenedRow(pathKey, openedRows)
    // deeply close all children
    const elements = gridRef?.querySelectorAll(`[data-parent-path^="${CSS.escape(pathKey)}"]`) || []
    for (const element of elements) {
      ;(element as HTMLElement).style.display = 'none'
      element.removeAttribute('data-cell')
      element.querySelector('[data-revealer]')?.removeAttribute('data-state')
    }
    revealerEl?.removeAttribute('data-state')
    return
  }

  const reveal = () => {
    addOpenedRow(pathKey, openedRows)
    // Guard for type-narrowing; pathKey exists only when isRevealed is true (level >= 0).
    if (!pathKey) return
    const elements = gridRef?.querySelectorAll(`[data-parent-path="${CSS.escape(pathKey)}"]`) || []
    for (const element of elements) {
      ;(element as HTMLElement).style.removeProperty('display')
      element.setAttribute('data-cell', 'true')
    }
    revealerEl?.setAttribute('data-state', 'open')
  }

  // children already loaded, or nothing to lazily load
  if (!pathKey || !loadChildren || childrenCache?.has(pathKey)) {
    reveal()
    return
  }
  const parentPath = rowsList.slice(0, level.value + 1).map((field, i) => ({ field, value: path.value[i] }))
  revealerEl?.setAttribute('data-loading', 'true')
  try {
    await loadChildren(parentPath, pathKey)
    // wait for the spliced-in rows to actually mount before revealing them
    await nextTick()
    reveal()
  } finally {
    revealerEl?.removeAttribute('data-loading')
  }
}
</script>
